import { useProctoredAttempt } from '../../components/focus-guard/FocusGuardContext';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useCourseModule } from './useCourseModule';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AuthenticatedVideo, AuthenticatedDocument, isObjectStorageUrl } from '../../hooks/useAuthenticatedFile';
import { LinkPlayerDialog } from '../../components/media/LinkPlayerDialog';
import { announceCertificate } from '../../components/course-builder/certificateLinks';
import {
  BookOpen,
  Play,
  FileText,
  ExternalLink,
  Clock,
  CheckCircle,
  Circle,
  Video,
  File,
  Link as LinkIcon,
  Award,
  HelpCircle,
  Loader2,
  ArrowLeft,
  Trophy,
  X,
  Check,
  RotateCcw,
  AlertCircle,
  Code,
  XCircle,
  Lock,
  Unlock
} from 'lucide-react';

export default function ModuleLearnerCourseView() {
  const { courseId } = useParams();
  const { api } = useAuth();
  const { apiBase, learnerBase } = useCourseModule();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState(null);
  const [unlockStatus, setUnlockStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeModule, setActiveModule] = useState(null);
  const [activeSubModule, setActiveSubModule] = useState(null);
  
  // Video player
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);
  
  // Quiz
  const [showQuizDialog, setShowQuizDialog] = useState(false);
  const [currentQuiz, setCurrentQuiz] = useState(null);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitting, setQuizSubmitting] = useState(false);
  const [quizResult, setQuizResult] = useState(null);
  useProctoredAttempt({ active: Boolean(currentQuiz) && !quizResult, label: 'quiz' });
  const [loadingQuiz, setLoadingQuiz] = useState(false);
  
  // Content Viewer
  const [showContentViewer, setShowContentViewer] = useState(false);
  const [viewerContent, setViewerContent] = useState(null);
  const [linkItem, setLinkItem] = useState(null);
  
  // Code execution
  const [codeOutputs, setCodeOutputs] = useState({});
  const [executingCode, setExecutingCode] = useState({});

  const fetchCourse = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`${apiBase}/builder-courses/${courseId}`);
      setCourse(response.data.course);
      setProgress(response.data.progress);
      setUnlockStatus(response.data.unlock_status);
      
      // Set first module as active
      if (response.data.course?.modules?.length > 0) {
        setActiveModule(response.data.course.modules[0]);
        if (response.data.course.modules[0].sub_modules?.length > 0) {
          setActiveSubModule(response.data.course.modules[0].sub_modules[0]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch course:', error);
      toast.error('Failed to load course');
      navigate(learnerBase);
    } finally {
      setLoading(false);
    }
  }, [api, courseId, navigate]);

  useEffect(() => {
    fetchCourse();
  }, [fetchCourse]);

  // Track content progress
  const trackContentProgress = async (contentId, action) => {
    try {
      const response = await api.post(
        `${apiBase}/builder-courses/${courseId}/progress/content/${contentId}?action=${action}`
      );
      announceCertificate(progress, response.data.overall_stats, navigate, `${learnerBase}/course/${courseId}/certificate`);
      setProgress(prev => ({
        ...prev,
        content_progress: {
          ...prev?.content_progress,
          [contentId]: response.data.content_progress
        },
        ...response.data.overall_stats
      }));
      
      // Update unlock status if returned
      if (response.data.unlock_status) {
        setUnlockStatus(response.data.unlock_status);
      }
    } catch (error) {
      console.error('Failed to track progress:', error);
    }
  };

  // Video handling
  const handlePlayVideo = async (video) => {
    // Get the full URL for the video
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    const videoUrl = video.url.startsWith('http') ? video.url : `${baseUrl}${video.url}`;
    
    // Check if it's an external video URL (YouTube, Vimeo, external sites)
    const isExternalVideo = videoUrl.includes('youtube.com') || 
                            videoUrl.includes('youtu.be') || 
                            videoUrl.includes('vimeo.com') ||
                            videoUrl.includes('coursera.org') ||
                            videoUrl.includes('udemy.com') ||
                            videoUrl.includes('linkedin.com/learning');
    
    // Check if it's a SharePoint/OneDrive video - they block iframe embedding
    const isSharePointVideo = videoUrl.includes('sharepoint.com') || 
                              videoUrl.includes('onedrive.com') || 
                              videoUrl.includes(':my.sharepoint') || 
                              videoUrl.includes('-my.sharepoint') ||
                              videoUrl.includes('1drv.ms') ||
                              videoUrl.includes('onedrive.live.com');
    
    const isLinkedVideo = video.type !== 'video_upload' && /^https?:\/\//i.test(video.url) && !isObjectStorageUrl(videoUrl);
    if (isExternalVideo || isSharePointVideo || isLinkedVideo) {
      // Play inside the page: a new tab counts as leaving the training (strike / sign-out)
      setLinkItem({ id: video.id, title: video.title, url: videoUrl, kind: 'video' });
      await trackContentProgress(video.id, 'view');
      return;
    }
    
    // Open uploaded videos in embedded viewer
    setViewerContent({
      type: 'video',
      title: video.title,
      url: videoUrl,
      videoType: video.type // 'video_upload' or 'video_url'
    });
    setShowContentViewer(true);
    
    // Track view
    await trackContentProgress(video.id, 'view');
    setCurrentVideo(video);
  };

  const handleVideoComplete = async () => {
    if (currentVideo) {
      await trackContentProgress(currentVideo.id, 'complete');
      toast.success('Video marked as complete');
    }
    setShowContentViewer(false);
    setViewerContent(null);
    setCurrentVideo(null);
  };

  // Document/Link handling
  const handleOpenDocument = async (doc) => {
    await trackContentProgress(doc.id, 'view');
    
    // Get the full URL for the document
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    let docUrl = doc.url;
    
    if (!docUrl.startsWith('http')) {
      docUrl = `${baseUrl}${docUrl}`;
    }
    
    // Check if it's a SharePoint/OneDrive URL - open in new tab (embedding is blocked)
    const isSharePointOrOneDrive = docUrl.includes('sharepoint.com') || 
                                    docUrl.includes('onedrive.com') || 
                                    docUrl.includes(':my.sharepoint') || 
                                    docUrl.includes('-my.sharepoint') ||
                                    docUrl.includes('1drv.ms') ||
                                    docUrl.includes('onedrive.live.com');
    
    if (isSharePointOrOneDrive) {
      // Show SharePoint/OneDrive files inside the page instead of a new tab
      setLinkItem({ id: doc.id, title: doc.title, url: docUrl, kind: 'document' });
      return;
    }
    
    // Open other documents in embedded viewer
    setViewerContent({
      type: 'document',
      title: doc.title,
      url: docUrl,
      fileType: doc.file_type?.toLowerCase() || 'pdf'
    });
    setShowContentViewer(true);
  };

  const handleMarkDocumentComplete = async (doc) => {
    await trackContentProgress(doc.id, 'complete');
    toast.success('Document marked as complete');
  };

  // Check if content is locked
  const isContentLocked = (contentId) => {
    if (!unlockStatus) return false;
    return unlockStatus.content?.[contentId] === false;
  };

  // Check if quiz is locked
  const isQuizLocked = (assessmentId) => {
    if (!unlockStatus) return false;
    return unlockStatus.quiz?.[assessmentId] === false;
  };

  // Check if module is locked
  const isModuleLocked = (moduleId) => {
    if (!unlockStatus) return false;
    return unlockStatus.module?.[moduleId] === false;
  };

  // Check if submodule is locked
  const isSubModuleLocked = (subModuleId) => {
    if (!unlockStatus) return false;
    return unlockStatus.submodule?.[subModuleId] === false;
  };

  // Code execution for coding questions
  const handleRunCode = async (questionId, language = 'python') => {
    const code = quizAnswers[questionId];
    if (!code || code.trim() === '') {
      toast.error('Please write some code first');
      return;
    }
    
    setExecutingCode({ ...executingCode, [questionId]: true });
    
    try {
      const response = await api.post(`${apiBase}/builder-courses/execute-code`, {
        code: code,
        language: language || 'python'
      });
      
      setCodeOutputs({
        ...codeOutputs,
        [questionId]: {
          success: response.data.success,
          output: response.data.output,
          error: response.data.error
        }
      });
    } catch (error) {
      setCodeOutputs({
        ...codeOutputs,
        [questionId]: {
          success: false,
          output: '',
          error: error.response?.data?.detail || 'Failed to execute code'
        }
      });
    } finally {
      setExecutingCode({ ...executingCode, [questionId]: false });
    }
  };
  
  // Helper to get language display name
  const getLanguageDisplayName = (lang) => {
    const names = {
      python: 'Python',
      javascript: 'JavaScript',
      java: 'Java',
      csharp: 'C#',
      cpp: 'C++',
      sql: 'SQL',
      html: 'HTML'
    };
    return names[lang?.toLowerCase()] || lang || 'Python';
  };
  
  // Helper to get placeholder text based on language
  const getCodePlaceholder = (lang) => {
    const placeholders = {
      python: '# Write your Python code here...',
      javascript: '// Write your JavaScript code here...',
      java: '// Write your Java code here...',
      csharp: '// Write your C# code here...',
      cpp: '// Write your C++ code here...',
      sql: '-- Write your SQL query here...',
      html: '<!-- Write your HTML here -->'
    };
    return placeholders[lang?.toLowerCase()] || '// Write your code here...';
  };

  // Quiz handling
  const handleStartQuiz = async (quizInfo) => {
    try {
      setLoadingQuiz(true);
      setQuizResult(null);
      setQuizAnswers({});
      
      const response = await api.get(`${apiBase}/builder-courses/${courseId}/quiz/${quizInfo.id}`);
      setCurrentQuiz(response.data);
      setQuizQuestions(response.data.questions || []);
      setShowQuizDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load quiz');
    } finally {
      setLoadingQuiz(false);
    }
  };

  const handleSubmitQuiz = async () => {
    if (!currentQuiz) return;
    
    // Check all questions answered
    const unanswered = quizQuestions.filter(q => !quizAnswers[q.id]);
    if (unanswered.length > 0) {
      toast.error(`Please answer all questions (${unanswered.length} remaining)`);
      return;
    }
    
    try {
      setQuizSubmitting(true);
      const response = await api.post(
        `${apiBase}/builder-courses/${courseId}/quiz/${currentQuiz.assessment_id}/submit`,
        quizAnswers
      );
      setQuizResult(response.data);
      announceCertificate(progress, response.data.overall_stats, navigate, `${learnerBase}/course/${courseId}/certificate`);
      setProgress(prev => ({
        ...prev,
        quiz_progress: { ...prev?.quiz_progress, [currentQuiz.assessment_id]: response.data.quiz_progress },
        ...response.data.overall_stats
      }));
      if (response.data.unlock_status) setUnlockStatus(response.data.unlock_status);
    } catch (error) {
      toast.error('Failed to submit quiz');
    } finally {
      setQuizSubmitting(false);
    }
  };

  const handleRetakeQuiz = () => {
    setQuizResult(null);
    setQuizAnswers({});
  };

  // Check if content is completed
  const isContentCompleted = (contentId) => {
    return progress?.content_progress?.[contentId]?.completed || false;
  };

  // Check if quiz is passed
  const isQuizPassed = (assessmentId) => {
    return progress?.quiz_progress?.[assessmentId]?.passed || false;
  };

  // Get content count
  const getContentCount = (target) => {
    const content = target?.content || {};
    return (
      (content.videos?.length || 0) +
      (content.documents?.length || 0) +
      (content.onedrive_links?.length || 0)
    );
  };

  // Render video embed
  const renderVideoEmbed = (video) => {
    const url = video.url || '';
    
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = url.includes('youtu.be') 
        ? url.split('/').pop()?.split('?')[0]
        : new URLSearchParams(new URL(url).search).get('v');
      return (
        <iframe
          src={`https://www.youtube.com/embed/${videoId}`}
          className="w-full aspect-video rounded-lg"
          allowFullScreen
          title={video.title}
        />
      );
    }
    
    if (url.includes('vimeo.com')) {
      const videoId = url.split('/').pop()?.split('?')[0];
      return (
        <iframe
          src={`https://player.vimeo.com/video/${videoId}`}
          className="w-full aspect-video rounded-lg"
          allowFullScreen
          title={video.title}
        />
      );
    }
    
    if (url.startsWith('/uploads/') || url.includes('/builder/files/') || url.endsWith('.mp4') || url.endsWith('.webm')) {
      // Use AuthenticatedVideo for object storage URLs
      if (isObjectStorageUrl(url)) {
        return (
          <AuthenticatedVideo
            src={url}
            controls
            className="w-full aspect-video rounded-lg bg-black"
          />
        );
      }
      // Direct URL for legacy uploads
      const fullUrl = url.startsWith('/') ? `${window.location.origin}${url}` : url;
      return (
        <video
          src={fullUrl}
          controls
          className="w-full aspect-video rounded-lg bg-black"
        >
          Your browser does not support video playback.
        </video>
      );
    }
    
    return (
      <div className="w-full aspect-video rounded-lg bg-muted flex flex-col items-center justify-center gap-4">
        <Video className="h-12 w-12 text-muted-foreground" />
        <p className="text-muted-foreground">External video</p>
        <Button variant="outline" onClick={() => setLinkItem({ id: video.id, title: video.title, url, kind: 'video' })}>
          <Play className="h-4 w-4 mr-2" />
          Play here
        </Button>
      </div>
    );
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </MainLayout>
    );
  }

  if (!course) {
    return (
      <MainLayout>
        <div className="text-center py-16">
          <h2 className="text-xl font-semibold">Course not found</h2>
          <Button asChild className="mt-4">
            <Link to={learnerBase}>Back to Courses</Link>
          </Button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="learner-course-view">
        {/* Course Header */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start gap-4 mb-4">
              <Button variant="ghost" size="icon" asChild>
                <Link to={learnerBase}>
                  <ArrowLeft className="h-5 w-5" />
                </Link>
              </Button>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline">{course.category_name}</Badge>
                  {progress?.status === 'completed' && (
                    <Badge className="bg-emerald-500">Completed</Badge>
                  )}
                </div>
                <h1 className="text-2xl font-bold">{course.title}</h1>
                <p className="text-muted-foreground mt-1">{course.description}</p>
              </div>
              {progress?.certificate_id && (
                <Button asChild>
                  <Link to={`${learnerBase}/course/${courseId}/certificate`} data-testid="course-view-certificate-btn">
                    <Award className="h-4 w-4 mr-2" />
                    View Certificate
                  </Link>
                </Button>
              )}
            </div>
            
            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>Course Progress</span>
                <span className="font-medium">{progress?.overall_progress_percent || 0}%</span>
              </div>
              <Progress value={progress?.overall_progress_percent || 0} className="h-2" />
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {progress?.completed_items || 0} of {progress?.total_items || 0} items completed
                </span>
                {progress?.overall_score > 0 && (
                  <span>Average Score: {progress.overall_score}%</span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Course Outline */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-lg">Course Outline</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[500px]">
                <Accordion type="single" collapsible className="px-4 pb-4">
                  {course.modules?.map((module, mIdx) => (
                    <AccordionItem key={module.id} value={module.id}>
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-2 text-left">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                            activeModule?.id === module.id ? 'bg-primary text-primary-foreground' : 'bg-muted'
                          }`}>
                            {mIdx + 1}
                          </div>
                          <span className="flex-1 truncate">{module.title}</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        {module.has_sub_modules ? (
                          <div className="space-y-1 pl-8">
                            {module.sub_modules?.map((sm) => {
                              const smCompleted = sm.content?.videos?.every(v => isContentCompleted(v.id)) &&
                                sm.content?.documents?.every(d => isContentCompleted(d.id)) &&
                                sm.content?.onedrive_links?.every(l => isContentCompleted(l.id)) &&
                                (!sm.has_quiz || isQuizPassed(sm.quiz_info?.id));
                              
                              return (
                                <button
                                  key={sm.id}
                                  onClick={() => {
                                    setActiveModule(module);
                                    setActiveSubModule(sm);
                                  }}
                                  className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors ${
                                    activeSubModule?.id === sm.id 
                                      ? 'bg-primary/10 text-primary' 
                                      : 'hover:bg-muted'
                                  }`}
                                >
                                  {smCompleted ? (
                                    <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                                  ) : (
                                    <Circle className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                  )}
                                  <span className="truncate">{sm.title}</span>
                                  <span className="text-xs text-muted-foreground ml-auto">
                                    {getContentCount(sm)}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setActiveModule(module);
                              setActiveSubModule(null);
                            }}
                            className="w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 ml-8 hover:bg-muted"
                          >
                            <FileText className="h-4 w-4" />
                            <span>View Content ({getContentCount(module)})</span>
                          </button>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Content Area */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>{activeSubModule?.title || activeModule?.title || 'Select a module'}</CardTitle>
              {(activeSubModule?.description || activeModule?.description) && (
                <CardDescription>
                  {activeSubModule?.description || activeModule?.description}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              {activeSubModule || (activeModule && !activeModule.has_sub_modules) ? (
                <div className="space-y-6">
                  {(() => {
                    const target = activeSubModule || activeModule;
                    const content = target?.content || {};
                    const videos = (content.videos || []).map(v => ({ ...v, contentType: 'video' }));
                    const documents = (content.documents || []).map(d => ({ ...d, contentType: 'document' }));
                    const links = (content.onedrive_links || []).map(l => ({ ...l, contentType: 'link' }));
                    
                    // Combine and sort by order
                    const allContent = [...videos, ...documents, ...links].sort((a, b) => (a.order || 0) - (b.order || 0));
                    
                    if (allContent.length === 0 && !target?.has_quiz) {
                      return (
                        <div className="text-center py-12 text-muted-foreground">
                          <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                          <p>No content in this section</p>
                        </div>
                      );
                    }
                    
                    return (
                      <>
                        {/* Unified Content List */}
                        {allContent.length > 0 && (
                          <div>
                            <h4 className="font-medium mb-3 flex items-center gap-2">
                              <BookOpen className="h-4 w-4" />
                              Course Content ({allContent.length} items)
                            </h4>
                            <div className="space-y-2">
                              {allContent.map((item, index) => {
                                const locked = isContentLocked(item.id);
                                
                                return (
                                <div 
                                  key={item.id}
                                  className={`flex items-center justify-between p-3 rounded-lg ${locked ? 'bg-muted/30 opacity-60' : 'bg-muted/50'}`}
                                >
                                  <div className="flex items-center gap-3">
                                    <span className="text-xs text-muted-foreground w-6">{index + 1}.</span>
                                    {locked ? (
                                      <Lock className="h-5 w-5 text-muted-foreground" />
                                    ) : isContentCompleted(item.id) ? (
                                      <CheckCircle className="h-5 w-5 text-emerald-500" />
                                    ) : item.contentType === 'video' ? (
                                      <Play className="h-5 w-5 text-blue-500" />
                                    ) : item.contentType === 'document' ? (
                                      <FileText className="h-5 w-5 text-orange-500" />
                                    ) : (
                                      <ExternalLink className="h-5 w-5 text-purple-500" />
                                    )}
                                    <span className={locked ? 'text-muted-foreground' : ''}>{item.title}</span>
                                    {item.contentType === 'video' && (
                                      <Badge variant="outline" className="text-xs">Video</Badge>
                                    )}
                                    {item.contentType === 'document' && (
                                      <Badge variant="outline" className="text-xs">{item.file_type?.toUpperCase() || 'DOC'}</Badge>
                                    )}
                                    {item.contentType === 'link' && (
                                      <Badge variant="outline" className="text-xs">Resource</Badge>
                                    )}
                                    {locked && (
                                      <Badge variant="secondary" className="text-xs">Complete previous to unlock</Badge>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {locked ? (
                                      <Button size="sm" variant="ghost" disabled>
                                        <Lock className="h-3 w-3 mr-1" />
                                        Locked
                                      </Button>
                                    ) : item.contentType === 'video' ? (
                                      <Button size="sm" onClick={() => handlePlayVideo(item)} data-testid={`watch-video-${item.id}`}>
                                        {isContentCompleted(item.id) ? 'Rewatch' : 'Watch'}
                                      </Button>
                                    ) : item.contentType === 'document' ? (
                                      <>
                                        {!isContentCompleted(item.id) && (
                                          <Button 
                                            size="sm" 
                                            variant="ghost"
                                            onClick={() => handleMarkDocumentComplete(item)}
                                            data-testid={`mark-done-doc-${item.id}`}
                                          >
                                            Mark Done
                                          </Button>
                                        )}
                                        <Button size="sm" variant="outline" onClick={() => handleOpenDocument(item)} data-testid={`open-doc-${item.id}`}>
                                          Open
                                        </Button>
                                      </>
                                    ) : (
                                      <>
                                        {!isContentCompleted(item.id) && (
                                          <Button 
                                            size="sm" 
                                            variant="ghost"
                                            onClick={() => trackContentProgress(item.id, 'complete')}
                                            data-testid={`mark-done-link-${item.id}`}
                                          >
                                            Mark Done
                                          </Button>
                                        )}
                                        <Button 
                                          size="sm" 
                                          variant="outline" 
                                          onClick={() => {
                                            trackContentProgress(item.id, 'view');
                                            // Open the link inside the page so the learner never leaves the training
                                            setLinkItem({ id: item.id, title: item.title, url: item.url, kind: 'link' });
                                          }}
                                          data-testid={`view-link-${item.id}`}
                                        >
                                          View
                                        </Button>
                                      </>
                                    )}
                                  </div>
                                </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        
                        {/* Quiz */}
                        {target?.has_quiz && target?.quiz_info && (
                          <div className="border-t pt-6">
                            {(() => {
                              const quizLocked = isQuizLocked(target.quiz_info.id);
                              return (
                            <div className={`flex items-center justify-between p-4 rounded-lg ${quizLocked ? 'bg-muted/30 opacity-60' : 'bg-primary/5'}`}>
                              <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-lg ${quizLocked ? 'bg-muted' : isQuizPassed(target.quiz_info.id) ? 'bg-emerald-100' : 'bg-primary/10'}`}>
                                  {quizLocked ? (
                                    <Lock className="h-5 w-5 text-muted-foreground" />
                                  ) : isQuizPassed(target.quiz_info.id) ? (
                                    <Trophy className="h-5 w-5 text-emerald-600" />
                                  ) : (
                                    <HelpCircle className="h-5 w-5 text-primary" />
                                  )}
                                </div>
                                <div>
                                  <h4 className="font-medium">{target.quiz_info.title || 'Section Quiz'}</h4>
                                  <p className="text-sm text-muted-foreground">
                                    {target.quiz_info.question_count} questions • 
                                    {target.quiz_info.passing_score}% to pass
                                    {isQuizPassed(target.quiz_info.id) && (
                                      <span className="text-emerald-600 font-medium"> • Passed!</span>
                                    )}
                                    {quizLocked && (
                                      <span className="text-muted-foreground"> • Complete all content to unlock</span>
                                    )}
                                  </p>
                                </div>
                              </div>
                              <Button 
                                onClick={() => handleStartQuiz(target.quiz_info)}
                                disabled={loadingQuiz || quizLocked}
                                data-testid="take-quiz-btn"
                              >
                                {quizLocked ? (
                                  <>
                                    <Lock className="h-4 w-4 mr-2" />
                                    Locked
                                  </>
                                ) : loadingQuiz ? (
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : isQuizPassed(target.quiz_info.id) ? (
                                  'Retake Quiz'
                                ) : (
                                  'Take Quiz'
                                )}
                              </Button>
                            </div>
                              );
                            })()}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Select a module from the outline to start learning</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* In-page player for OneDrive/SharePoint/YouTube links (never a new tab) */}
        <LinkPlayerDialog
          item={linkItem}
          onClose={() => setLinkItem(null)}
          completed={linkItem ? isContentCompleted(linkItem.id) : false}
          onComplete={async () => {
            await trackContentProgress(linkItem.id, 'complete');
            toast.success('Marked as complete');
            setLinkItem(null);
          }}
        />

        {/* Embedded Content Viewer Dialog */}
        <Dialog open={showContentViewer} onOpenChange={(open) => {
          if (!open) {
            setShowContentViewer(false);
            setViewerContent(null);
            setCurrentVideo(null);
          }
        }}>
          <DialogContent className="max-w-5xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {viewerContent?.type === 'video' ? (
                  <Video className="h-5 w-5 text-blue-500" />
                ) : (
                  <FileText className="h-5 w-5 text-orange-500" />
                )}
                {viewerContent?.title}
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              {viewerContent?.type === 'video' && (
                <div className="aspect-video bg-black rounded-lg overflow-hidden">
                  {viewerContent.videoType === 'video_upload' ? (
                    <video 
                      controls 
                      className="w-full h-full" 
                      controlsList="nodownload"
                      onContextMenu={(e) => e.preventDefault()}
                    >
                      <source src={viewerContent.url} />
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    // For YouTube/Vimeo URLs, create iframe
                    (() => {
                      const url = viewerContent.url;
                      let embedUrl = url;
                      
                      // Convert YouTube URL to embed format
                      if (url.includes('youtube.com/watch')) {
                        const videoId = new URL(url).searchParams.get('v');
                        embedUrl = `https://www.youtube.com/embed/${videoId}`;
                      } else if (url.includes('youtu.be/')) {
                        const videoId = url.split('youtu.be/')[1]?.split('?')[0];
                        embedUrl = `https://www.youtube.com/embed/${videoId}`;
                      } else if (url.includes('vimeo.com')) {
                        const videoId = url.match(/vimeo\.com\/(\d+)/)?.[1];
                        embedUrl = `https://player.vimeo.com/video/${videoId}`;
                      }
                      
                      return (
                        <iframe
                          src={embedUrl}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          title={viewerContent.title}
                        />
                      );
                    })()
                  )}
                </div>
              )}
              
              {viewerContent?.type === 'document' && (
                <div className="h-[70vh] bg-muted rounded-lg overflow-hidden">
                  {(() => {
                    const url = viewerContent.url || '';
                    const isSharePoint = url.includes('sharepoint.com') || url.includes('onedrive.com') || url.includes(':my.sharepoint') || url.includes('-my.sharepoint');
                    
                    // Handle object storage URLs (uploaded documents)
                    if (isObjectStorageUrl(url)) {
                      return (
                        <AuthenticatedDocument
                          src={url}
                          className="w-full h-full"
                          title={viewerContent.title}
                          fileType={viewerContent.fileType}
                        />
                      );
                    }
                    
                    // Handle OneDrive/SharePoint URLs
                    if (isSharePoint) {
                      // Check if it's already an embed URL
                      if (url.includes('embed.aspx') || url.includes('_layouts/15/embed')) {
                        return (
                          <iframe
                            src={url}
                            className="w-full h-full"
                            title={viewerContent.title}
                            allow="fullscreen"
                            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                          />
                        );
                      }
                      
                      // Try to transform sharing URLs to embed format
                      // SharePoint sharing URLs typically look like:
                      // https://tenant.sharepoint.com/:v:/g/personal/user/ENCODED_ID
                      // or https://tenant-my.sharepoint.com/personal/user/_layouts/15/onedrive.aspx?id=...
                      
                      // For standard sharing links, try the embed transformation
                      let embedUrl = url;
                      
                      // Pattern 1: Direct sharing link with :v: or :w: or :b: format
                      // These need to be converted to embed format
                      if (url.match(/sharepoint\.com\/:[\w]:\/[grs]\//)) {
                        // Replace the sharing indicator with embed path
                        embedUrl = url.replace(/(:[\w]:\/[grs]\/)/, '/_layouts/15/embed.aspx?');
                        // Try adding the original URL as source
                        embedUrl = url.replace(/\?.*$/, '') + '?action=embedview&wdStartOn=1';
                      }
                      
                      // Pattern 2: If URL has resid or id parameter, construct embed URL
                      const urlObj = new URL(url);
                      const resid = urlObj.searchParams.get('resid') || urlObj.searchParams.get('id');
                      if (resid) {
                        const baseUrl = urlObj.origin;
                        embedUrl = `${baseUrl}/_layouts/15/embed.aspx?UniqueId=${encodeURIComponent(resid)}`;
                      }
                      
                      return (
                        <div className="w-full h-full flex flex-col">
                          <iframe
                            src={embedUrl}
                            className="w-full flex-1"
                            title={viewerContent.title}
                            allow="fullscreen"
                            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                          />
                          <div className="p-2 bg-amber-50 border-t border-amber-200 text-xs text-amber-800 flex items-center gap-2">
                            <AlertCircle className="h-3 w-3" />
                            <span>
                              If content doesn't load, ask your admin to provide the SharePoint embed URL 
                              (Share → Embed → Copy code)
                            </span>
                          </div>
                        </div>
                      );
                    }
                    
                    // Standard PDF handling
                    if (viewerContent.fileType === 'pdf') {
                      return (
                        <iframe
                          src={`${url}#toolbar=0&navpanes=0`}
                          className="w-full h-full"
                          title={viewerContent.title}
                        />
                      );
                    }
                    
                    // Use Google Docs Viewer for Word documents and other files
                    return (
                      <iframe
                        src={`https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`}
                        className="w-full h-full"
                        title={viewerContent.title}
                      />
                    );
                  })()}
                </div>
              )}
            </div>
            <DialogFooter className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                This content is protected and cannot be downloaded
              </p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => {
                  setShowContentViewer(false);
                  setViewerContent(null);
                  setCurrentVideo(null);
                }}>
                  Close
                </Button>
                {viewerContent?.type === 'video' && currentVideo && (
                  <Button onClick={handleVideoComplete}>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Mark Complete
                  </Button>
                )}
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Quiz Dialog */}
        <Dialog open={showQuizDialog} onOpenChange={setShowQuizDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh]" data-testid="quiz-dialog">
            <DialogHeader>
              <DialogTitle>{currentQuiz?.title || 'Quiz'}</DialogTitle>
              {!quizResult && (
                <DialogDescription>
                  {currentQuiz?.previous_attempts > 0 && (
                    <span>
                      Attempt {currentQuiz.previous_attempts + 1} of {currentQuiz.max_attempts} • 
                      Best score: {currentQuiz.best_score}%
                    </span>
                  )}
                </DialogDescription>
              )}
            </DialogHeader>
            <ScrollArea className="max-h-[55vh] pr-4">
              <div className="space-y-6 py-4">
                {quizResult ? (
                  <div className="text-center py-8">
                    <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center text-3xl font-bold ${
                      quizResult.passed 
                        ? 'bg-emerald-100 text-emerald-600' 
                        : 'bg-red-100 text-red-600'
                    }`} data-testid="quiz-result-score">
                      {quizResult.score}%
                    </div>
                    <h3 className="text-xl font-semibold mt-4">
                      {quizResult.passed ? 'Congratulations!' : 'Keep Trying!'}
                    </h3>
                    <p className="text-muted-foreground mt-2">
                      {quizResult.passed 
                        ? 'You passed this assessment.'
                        : `You need ${quizResult.passing_score}% to pass.`
                      }
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {quizResult.earned_points} / {quizResult.total_points} points
                    </p>
                    
                    {/* Feedback */}
                    <div className="mt-6 text-left space-y-3">
                      {quizResult.feedback?.map((fb, idx) => (
                        <div 
                          key={fb.question_id}
                          className={`p-3 rounded-lg ${fb.is_correct ? 'bg-emerald-50' : 'bg-red-50'}`}
                        >
                          <div className="flex items-center gap-2">
                            {fb.is_correct ? (
                              <Check className="h-4 w-4 text-emerald-600" />
                            ) : (
                              <X className="h-4 w-4 text-red-600" />
                            )}
                            <span className="font-medium text-sm">Question {idx + 1}</span>
                          </div>
                          {fb.explanation && (
                            <p className="text-xs text-muted-foreground mt-1">{fb.explanation}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  quizQuestions.map((q, idx) => (
                    <div key={q.id} className="space-y-3" data-testid={`quiz-question-${idx}`}>
                      <div className="flex items-start gap-2">
                        <span className="font-medium min-w-[24px]">{idx + 1}.</span>
                        <div className="flex-1">
                          <p className="font-medium">{q.question}</p>
                          <Badge variant="outline" className="mt-1 text-xs" data-testid={`quiz-question-type-${idx}`}>
                            {q.type === 'mcq' ? 'Multiple Choice' : q.type === 'true_false' ? 'True/False' : q.type === 'short_answer' ? 'Short Answer' : 'Coding'}
                          </Badge>
                        </div>
                      </div>
                      
                      {q.type === 'mcq' && (
                        <RadioGroup
                          value={quizAnswers[q.id] || ''}
                          onValueChange={(v) => setQuizAnswers({ ...quizAnswers, [q.id]: v })}
                          className="pl-8"
                        >
                          {q.options?.map(opt => (
                            <div key={opt.id} className="flex items-center space-x-2">
                              <RadioGroupItem value={opt.id} id={`${q.id}-${opt.id}`} />
                              <Label htmlFor={`${q.id}-${opt.id}`}>{opt.text}</Label>
                            </div>
                          ))}
                        </RadioGroup>
                      )}
                      
                      {q.type === 'true_false' && (
                        <RadioGroup
                          value={quizAnswers[q.id] || ''}
                          onValueChange={(v) => setQuizAnswers({ ...quizAnswers, [q.id]: v })}
                          className="pl-8"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="true" id={`${q.id}-true`} />
                            <Label htmlFor={`${q.id}-true`}>True</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="false" id={`${q.id}-false`} />
                            <Label htmlFor={`${q.id}-false`}>False</Label>
                          </div>
                        </RadioGroup>
                      )}
                      
                      {q.type === 'short_answer' && (
                        <div className="pl-8 mt-3">
                          <div className="border rounded-lg overflow-hidden">
                            <div className="bg-muted px-3 py-2 border-b flex items-center gap-2">
                              <FileText className="h-4 w-4" />
                              <span className="text-sm font-medium">Your Answer</span>
                            </div>
                            <textarea
                              className="w-full p-3 text-sm min-h-[120px] resize-none focus:outline-none"
                              placeholder="Type your answer here... Be clear and include key concepts."
                              data-testid={`short-answer-input-${idx}`}
                              value={quizAnswers[q.id] || ''}
                              onChange={(e) => setQuizAnswers({ ...quizAnswers, [q.id]: e.target.value })}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            Write 1-3 sentences. Include relevant concepts and terminology.
                          </p>
                        </div>
                      )}
                      
                      {q.type === 'coding' && (
                        <div className="pl-8 mt-3 space-y-3">
                          {/* Code Template */}
                          {q.code_template && (
                            <div>
                              <p className="text-sm text-muted-foreground mb-1">Starter Code:</p>
                              <pre className="bg-slate-900 text-slate-100 p-3 rounded text-sm overflow-x-auto font-mono">
                                {q.code_template}
                              </pre>
                            </div>
                          )}
                          
                          {/* Instructions */}
                          {q.instructions && (
                            <p className="text-sm text-muted-foreground bg-blue-50 p-2 rounded">
                              {q.instructions}
                            </p>
                          )}
                          
                          {/* Code Editor */}
                          <div className="border rounded-lg overflow-hidden">
                            <div className="bg-slate-800 text-slate-200 px-3 py-2 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Code className="h-4 w-4" />
                                <span className="text-sm font-medium">{getLanguageDisplayName(q.language)} Code Editor</span>
                              </div>
                              <Button 
                                size="sm" 
                                variant="secondary"
                                onClick={() => handleRunCode(q.id, q.language)}
                                disabled={executingCode[q.id]}
                                className="h-7"
                              >
                                {executingCode[q.id] ? (
                                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                ) : (
                                  <Play className="h-3 w-3 mr-1" />
                                )}
                                Run Code
                              </Button>
                            </div>
                            <textarea
                              className="w-full p-3 font-mono text-sm min-h-[150px] bg-slate-950 text-slate-100 resize-none focus:outline-none"
                              placeholder={getCodePlaceholder(q.language)}
                              data-testid={`coding-answer-input-${idx}`}
                              value={quizAnswers[q.id] || q.code_template || ''}
                              onChange={(e) => setQuizAnswers({ ...quizAnswers, [q.id]: e.target.value })}
                              spellCheck="false"
                            />
                          </div>
                          
                          {/* Output Panel */}
                          {codeOutputs[q.id] && (
                            <div className="border rounded-lg overflow-hidden">
                              <div className={`px-3 py-2 flex items-center gap-2 ${codeOutputs[q.id].success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                {codeOutputs[q.id].success ? (
                                  <CheckCircle className="h-4 w-4" />
                                ) : (
                                  <XCircle className="h-4 w-4" />
                                )}
                                <span className="text-sm font-medium">
                                  {codeOutputs[q.id].success ? 'Output' : 'Error'}
                                </span>
                              </div>
                              <pre className="p-3 text-sm font-mono bg-slate-50 overflow-x-auto max-h-[150px]">
                                {codeOutputs[q.id].output || codeOutputs[q.id].error || 'No output'}
                              </pre>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
            <DialogFooter>
              {quizResult ? (
                <>
                  {!quizResult.passed && quizResult.attempt_number < currentQuiz?.max_attempts && (
                    <Button variant="outline" onClick={handleRetakeQuiz}>
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Try Again
                    </Button>
                  )}
                  <Button onClick={() => setShowQuizDialog(false)} data-testid="quiz-result-close-btn">Close</Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setShowQuizDialog(false)} data-testid="quiz-cancel-btn">Cancel</Button>
                  <Button onClick={handleSubmitQuiz} disabled={quizSubmitting} data-testid="submit-quiz-btn">
                    {quizSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Submit Quiz
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
